// MACHINERY V2 (Stroje) — profile tabs (M2.4; below-1280 treatment M2.16d).
//
// TWO TREATMENTS, ONE TAB LIST.
//   >= 1280  pill tabs, 34px, fully rounded, active = module teal fill.
//            Artboard 0504 / machinery-profile-mash.jsx. UNCHANGED by M2.16d.
//   <  1280  a horizontally SCROLLING row with a right-edge fade; active tab =
//            var(--mod-accent) text + a 2px underline. mobile/README.md
//            §"Profil (20–24)" and board 20, whose values this branch carries
//            verbatim (machinery-mobile.jsx:557-566: padding 10px 9px 11px,
//            fontSize 12.5, underline inset 7px, height 2).
//
// The scroller is the kit's ChipScroller — NOT a fork. Its header names this tab
// bar as one of the four consumers it was built for, and it already solves the
// two things a hand-rolled row gets wrong: the hidden scrollbar (an 8px thumb on
// a 40px row) and the trailing spacer that stops the permanent 30px gradient from
// sitting on top of the last tab.
//
// ============================================================
//  >>> THE TAB BAR COMES FROM BOARD 20. BOARDS 21 AND 24 ARE STALE. <<<
// ============================================================
// DO NOT "fix" the order or the first label to match boards 21/24. Those boards
// draw "Živé dáta · Servisné správy · Diely a materiál · Dokumen…" — a different
// first label AND a different order. Board 20 draws "Prehľad · Servisné správy ·
// Dokumenty · Špecifikácie" (+ Diely under the fade). Board 20 wins, on three
// grounds:
//   1. mobile/README.md says tabs 21–24 are "unchanged from the earlier phone
//      boards", i.e. 21–24 carry a tab bar from an OLDER set; 20 is the new
//      artifact of this pass.
//   2. Board 20's order is EXACTLY our shipped contract order
//      (profileTabContract.ts: overview -> serviceReports -> documents ->
//      specifications -> parts). Boards 21/24's is not.
//   3. "Živé dáta" is the label manager ruling Q1-M2.16 replaced with Prehľad.
// This component does not own the order in any case — profileTabContract.ts
// does, and this paints the list it is handed.
//
// PRESENTATIONAL ONLY. Which tabs exist and which is active are decided in
// profileTabContract.ts and owned by MachineProfileV2. That is why a tab can
// never be rendered here that the gate upstream would have hidden.
//
// 0504 also draws a "VIAC ·" affordance at the right end of the desktop row. It
// is still NOT built: it exists in the artboard to overflow a tab list, and the
// five pills fit at >= 1280. Below 1280 the overflow problem is real and is
// solved by scrolling + the fade, which is the same answer the whole mobile
// package gives every chip row.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import ChipScroller from "../mobile/ChipScroller";
import {
  MachineryV2ProfileTab,
  PROFILE_TAB_LABEL_KEYS,
} from "./profileTabContract";

export interface ProfileTabsProps {
  tabs: MachineryV2ProfileTab[];
  active: MachineryV2ProfileTab;
  onChange: (tab: MachineryV2ProfileTab) => void;
  /** `useV2MediaQuery(V2_BELOW_DESKTOP)`, read ONCE by ProfileBody and passed
   *  down. Defaults to the >= 1280 pills, so a caller that does not pass it
   *  gets byte-identical desktop markup. */
  belowDesktop?: boolean;
}

const ProfileTabs: React.FC<ProfileTabsProps> = ({
  tabs,
  active,
  onChange,
  belowDesktop = false,
}) => {
  const { translate } = useTranslation("myMachinery");

  const tabButton = (tab: MachineryV2ProfileTab) => {
    const on = tab === active;
    const shared = {
      key: tab,
      type: "button" as const,
      role: "tab",
      "aria-selected": on,
      id: `machinery-v2-tab-${tab}`,
      "aria-controls": "machinery-v2-tabpanel",
      onClick: () => onChange(tab),
    };
    const label = translate(PROFILE_TAB_LABEL_KEYS[tab]);

    if (!belowDesktop) {
      // >= 1280 — the M2.4 pill, unchanged.
      return (
        <button
          {...shared}
          style={{
            height: 34,
            padding: "0 14px",
            borderRadius: 999,
            border: "1px solid " + (on ? "var(--mod-accent)" : "transparent"),
            background: on ? "var(--mod-accent)" : "transparent",
            color: on ? "var(--mod-accent-fg)" : "var(--muted)",
            fontFamily: "inherit",
            fontSize: 13,
            fontWeight: on ? 600 : 500,
            cursor: "pointer",
            whiteSpace: "nowrap",
            transition: "background 0.12s, color 0.12s, border-color 0.12s",
          }}
        >
          {label}
        </button>
      );
    }

    // < 1280 — board 20's underline tab. The 2px rule is a child of the button
    // rather than a border, so it can carry the board's 7px horizontal inset
    // without shrinking the touch target.
    return (
      <button
        {...shared}
        style={{
          position: "relative",
          // >= 44px on BOTH axes below 1280. The board's own padding gives ~37,
          // and there is deliberately no `minWidth: 0` after this pair — a later
          // minWidth in the same object would silently void the first.
          minHeight: 44,
          minWidth: 44,
          padding: "10px 9px 11px",
          border: 0,
          background: "transparent",
          color: on ? "var(--mod-accent)" : "var(--muted)",
          fontFamily: "inherit",
          fontSize: 12.5,
          fontWeight: on ? 600 : 400,
          cursor: "pointer",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {label}
        {on && (
          <span
            aria-hidden
            style={{
              position: "absolute",
              left: 7,
              right: 7,
              bottom: 0,
              height: 2,
              background: "var(--mod-accent)",
            }}
          />
        )}
      </button>
    );
  };

  const label = translate("myMachinery:v2.profile.tabsLabel");

  if (!belowDesktop) {
    return (
      <div
        role="tablist"
        aria-label={label}
        style={{
          display: "flex",
          gap: 4,
          alignItems: "center",
          flexWrap: "wrap",
          minWidth: 0,
        }}
      >
        {tabs.map(tabButton)}
      </div>
    );
  }

  return (
    <div style={{ borderBottom: "1px solid var(--border)", minWidth: 0 }}>
      {/* THE TABLIST ROLE GOES ON THE SCROLLER, NOT ON THE WRAPPER. ARIA
          requires a `role="tab"` to be OWNED by its tablist, and ChipScroller
          puts two generic divs between this element and the buttons — a role
          here would report a tablist with zero tabs. `role` / `ariaLabel` are
          passed IN so they land on the element the buttons are really children
          of (ChipScroller.tsx).

          fadeTo is var(--bg) because this row sits on the PAGE, not inside a
          card: ProfileBody's <main> paints nothing of its own, so the surface
          under the tabs is .machinery-v2-root's var(--bg). A wrong value here is
          a visible grey smear rather than a fade. */}
      <ChipScroller
        role="tablist"
        ariaLabel={label}
        fadeTo="var(--bg)"
        gap={2}
        paddingInline={0}
      >
        {tabs.map(tabButton)}
      </ChipScroller>
    </div>
  );
};

export default ProfileTabs;
